<template>
    <MainContent
        title="Listado de invitaciones"
        :disabled="loading || loadingGenerar"
    >
        <template #header>
            <InvitacionesToolbar
                :filters="filters"
                :eventos="eventos"
                :disabled="loading || loadingGenerar"
                :puede-generar="puedeGenerar"
                @update:filters="onFiltersInput"
                @generar="onGenerar"
            >
                <template #description>
                    <p class="tw:text-sm tw:text-gray-500 tw:m-0">
                        Pedidos con invitaciones de esta sesión. Marca los pedidos y pulsa
                        «Generar invitaciones»; despliega un pedido para ver sus entradas.
                    </p>
                </template>
            </InvitacionesToolbar>
        </template>

        <Card v-if="error" customClasses="tw:mb-4">
            <p class="tw:text-red-600 tw:text-sm tw:m-0">{{ error }}</p>
        </Card>

        <PedidosTable
            :columns="columns"
            :pedidos="pedidos"
            :loading="loading"
            :seleccionados="seleccionados"
            :expanded-key="expandedKey"
            @toggle-seleccion="toggleSeleccion"
            @toggle-expand="toggleExpand"
        />

        <TableFooter
            v-if="meta && typeof meta.total === 'number'"
            class="tw:mt-6"
            :value="filters"
            :meta="meta"
            @input="onFiltersInput"
        />

        <!--
          Modales legacy preservados tal cual. Se montan sólo cuando hay algo
          que mostrar, así que hay que esperar un tick antes de abrirlos con
          vue-js-modal (el registro ocurre al montar ModalComponent).
        -->
        <InvitacionesCopiadas
            v-if="generatedOrders.length"
            :orders="generatedOrders"
            :evento-id="props.evento_id"
            :sesion-id="props.sesion_id"
            v-on:close="closeModal('invitaciones-copiadas')"
        />
        <ErrorCopiarInvitaciones
            v-if="copyErrors.length"
            :errors="copyErrors"
            v-on:close="closeModal('error-copiar-invitaciones')"
        />
    </MainContent>
</template>

<script setup>
    import { ref, nextTick, getCurrentInstance } from 'vue';

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import TableFooter from '@/modules/admin/ui/table/TableFooter.vue';
    import InvitacionesToolbar from '@/modules/admin/events/sesiones/components/invitaciones/InvitacionesToolbar.vue';
    import PedidosTable from '@/modules/admin/events/sesiones/components/invitaciones/PedidosTable.vue';
    import InvitacionesCopiadas from '@/modules/admin/events/sesiones/components/invitaciones/modals/InvitacionesCopiadasModal.vue';
    import ErrorCopiarInvitaciones from '@/modules/admin/events/sesiones/components/invitaciones/modals/ErrorCopiarInvitacionesModal.vue';

    import { useListadoInvitaciones } from '@/composables/admin/events/sesiones/useListadoInvitaciones';

    const props = defineProps({
        evento_id: {
            type: [Number, String],
            required: true
        },
        sesion_id: {
            type: [Number, String],
            required: true
        }
    });

    const vm = getCurrentInstance().proxy;

    const {
        pedidos,
        loading,
        loadingGenerar,
        error,
        filters,
        meta,
        eventos,
        seleccionados,
        puedeGenerar,
        toggleSeleccion,
        expandedKey,
        toggleExpand,
        generarInvitaciones,
        columns
    } = useListadoInvitaciones({
        eventoId: props.evento_id,
        sesionId: props.sesion_id
    });

    const generatedOrders = ref([]);
    const copyErrors = ref([]);

    /**
     * La toolbar y el footer emiten el filtro que cambió. Se aplica sobre el
     * objeto que gobierna `useQueryList` para que su watcher vuelva a pedir el
     * listado (con su debounce).
     */
    function onFiltersInput(next) {
        Object.assign(filters.value, next);
    }

    /**
     * Genera las invitaciones de los pedidos marcados y abre el modal que
     * corresponda. La vista es la única que conoce los modales; el composable
     * sólo devuelve el resultado.
     *
     * El modal de error lista los pedidos que no se pudieron copiar, así que
     * sólo aplica cuando el endpoint devolvió esa lista (422). Si el fallo no
     * trae pedidos —error de red, 500 sin payload— no hay nada que listar: el
     * modal ni siquiera está montado (su `v-if` depende de `copyErrors`) y
     * abrirlo no haría nada, así que se avisa con un toast genérico.
     */
    async function onGenerar() {
        const result = await generarInvitaciones();

        if (result.ok) {
            generatedOrders.value = result.orders;
            await nextTick();
            vm.$modal.show('invitaciones-copiadas');
            return;
        }

        if (result.errors.length > 0) {
            copyErrors.value = result.errors;
            await nextTick();
            vm.$modal.show('error-copiar-invitaciones');
            return;
        }

        copyErrors.value = [];
        vm.$toast.error(result.message || 'No se pudieron generar las invitaciones');
    }

    function closeModal(name) {
        vm.$modal.hide(name);
    }
</script>
